import React, { Component } from "react";
import { Modal, Spinner, Alert } from "react-bootstrap";
import { marked } from "marked";
import { MathJax, MathJaxContext } from "better-react-mathjax";

class AiSimilarQuestionModels2 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modalShow: false,
      similar: [],
      loading: false,
      explanationLoading: false,
      detailedLoading: false,
      error: null,
      currentIndex: 0,
      showExplanation: false,
      showDetailedExplanation: false,
      selectedOption: null,
      answered: false,
    };
  }

  // Helpers reused across methods to avoid double-encoding and to produce
  // application/x-www-form-urlencoded style encoding (spaces -> +).
  safeDecode = (s) => {
    if (!s || typeof s !== "string") return s;
    let out = s;
    try {
      for (let i = 0; i < 3; i++) {
        const dec = decodeURIComponent(out);
        if (dec === out) break;
        out = dec;
      }
    } catch (e) {
      return s;
    }
    return out;
  };

  encodeForm = (s) => {
    try {
      if (s === null || typeof s === "undefined") return "";
      const dec = this.safeDecode(String(s));
      return encodeURIComponent(dec).replace(/%20/g, "+");
    } catch (e) {
      return encodeURIComponent(String(s)).replace(/%20/g, "+");
    }
  };

  handleSelectOption = (key) => {
    if (this.state.answered) return;
    this.setState({ selectedOption: key, answered: true });
  };

  // ✅ cache key by question_id
  getCacheKey = () => {
    const questionId = this.props.question_id || "no_id";
    return `ai_similar_questions_${questionId}`;
  };

  open = () => this.setState({ modalShow: true });
  close = () =>
    this.setState({
      modalShow: false,
      currentIndex: 0,
      showExplanation: false,
      showDetailedExplanation: false,
    });

  loadFromCache = () => {
    try {
      const cached = localStorage.getItem(this.getCacheKey());
      if (!cached) return null;
      return JSON.parse(cached);
    } catch (e) {
      return null;
    }
  };

  saveToCache = (data) => {
    try {
      localStorage.setItem(this.getCacheKey(), JSON.stringify(data));
    } catch (e) {
      // ignore
    }
  };

  // ✅ ONLY TEXT should be sent to API
  cleanQuestionForApi = (input) => {
    if (!input) return "";

    let text = String(input);

    // ✅ 1) Remove HTML tags completely
    text = text.replace(/<\/?[^>]+(>|$)/g, " ");

    // ✅ 2) Convert common HTML entities
    text = text
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    // ✅ 3) Remove MathML tags if present (send only text)
    text = text.replace(/<math[\s\S]*?<\/math>/g, " ");

    // ✅ 4) Normalize extra spaces
    text = text.replace(/\s+/g, " ").trim();

    return text;
  };

  // Normalize option/question/explanation text for rendering math correctly
  normalizeMathText = (input) => {
    if (!input) return "";
    let text = String(input);

    // Convert any inline MathML to LaTeX (best-effort) before further processing
    const mathmlMatches = [];
    text = text.replace(/<math[\s\S]*?<\/math>/gi, (m) => {
      mathmlMatches.push(m);
      return `@@MATHML_REPLACE_${mathmlMatches.length - 1}@@`;
    });

    const mathMLToLatex = (mathString) => {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(mathString, "application/xml");

        const convert = (node) => {
          if (!node) return "";
          if (node.nodeType === 3) return node.nodeValue || "";
          const name = node.nodeName.toLowerCase();

          if (name === "msup") {
            const a = node.childNodes[0] ? convert(node.childNodes[0]) : "";
            const b = node.childNodes[1] ? convert(node.childNodes[1]) : "";
            return `${a}^{${b}}`;
          }
          if (name === "msub") {
            const a = node.childNodes[0] ? convert(node.childNodes[0]) : "";
            const b = node.childNodes[1] ? convert(node.childNodes[1]) : "";
            return `${a}_{${b}}`;
          }
          if (name === "mfrac") {
            const a = node.childNodes[0] ? convert(node.childNodes[0]) : "";
            const b = node.childNodes[1] ? convert(node.childNodes[1]) : "";
            return `\\frac{${a}}{${b}}`;
          }
          if (name === "msqrt") {
            const inner = [...node.childNodes].map(convert).join("");
            return `\\sqrt{${inner}}`;
          }
          if (name === "mfenced") {
            const inner = [...node.childNodes].map(convert).join("");
            return `(${inner})`;
          }
          if (name === "mrow") return [...node.childNodes].map(convert).join("");
          if (name === "mi" || name === "mn" || name === "mo") return node.textContent || "";
          // fallback: join children
          return [...node.childNodes].map(convert).join("");
        };

        const latex = convert(doc.documentElement || doc.firstChild);
        return latex || "";
      } catch (e) {
        return "";
      }
    };

    // restore converted MathML as LaTeX wrapped in $...$
    if (mathmlMatches.length) {
      mathmlMatches.forEach((m, idx) => {
        const latex = mathMLToLatex(m);
        // wrap as inline math
        text = text.replace(`@@MATHML_REPLACE_${idx}@@`, latex ? `$${latex}$` : "");
      });
    }

    // Unescape common HTML entities
    text = text
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    // Preserve LaTeX delimiters: convert escaped \( \) to $...$ and \[ \] to $$..$$
    text = text.replace(/\\\(/g, "$");
    text = text.replace(/\\\)/g, "$");
    text = text.replace(/\\\[/g, "$$");
    text = text.replace(/\\\]/g, "$$");

    // Remove any remaining HTML tags but keep content
    text = text.replace(/<[^>]+>/g, " ");

    // Normalize whitespace but preserve paragraph breaks/newlines so markdown
    // structures (headings, lists, horizontal rules) survive.
    // - Normalize CRLF to LF
    // - Collapse 3+ newlines into 2
    // - Collapse multiple spaces/tabs within a line
    text = text.replace(/\r\n/g, "\n");
    text = text.replace(/\n{3,}/g, "\n\n");
    text = text
      .split("\n")
      .map((line) => line.replace(/[ \t]+/g, " ").trim())
      .join("\n");
    // Ensure at most one blank line between paragraphs and trim edges
    text = text.replace(/\n\s*\n+/g, "\n\n").trim();

    return text;
  };

  fetchSimilarFromApi = async () => {
    const rawQuery = this.props.question || this.props.triggerQueryText || "sample question";
    const decodedRaw = this.safeDecode(rawQuery);

    // ✅ Prepare question for API: convert MathML to LaTeX and preserve markdown
    // Use normalizeMathText which performs MathML->LaTeX conversion and preserves paragraph breaks.
    let query = this.normalizeMathText(decodedRaw);
    if (!query) query = this.cleanQuestionForApi(decodedRaw);

    // Use props when provided (safe-decode to avoid double-encoding issues)
    const studentClass = this.safeDecode(this.props.student_class || this.props.className || "");
    const subject = this.safeDecode(this.props.subject || "");
    const chapter = this.safeDecode(this.props.chapter || "");
    const exam = this.safeDecode("JEE Main");

    this.setState({
      loading: true,
      error: null,
      similar: [],
      currentIndex: 0,
      showExplanation: false,
      showDetailedExplanation: false,
      selectedOption: null,
      answered: false,
    });

    this.open();

    try {
      // encode as application/x-www-form-urlencoded style (spaces -> +)
      const qParam = this.encodeForm(query);
      const scParam = this.encodeForm(studentClass);
      const subjParam = this.encodeForm(subject);
      const chapParam = this.encodeForm(chapter);
      const examParam = this.encodeForm(exam);

      // Use GET endpoint to fetch similar questions
      const url = `https://ai.rizee.in/get-similar-questions-3?question=${qParam}&student_class=${scParam}&subject=${subjParam}&chapter=${chapParam}&exam=${examParam}&count=3&difficulty=Hard`;
   
      const res = await fetch(url, { method: "GET" });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API error ${res.status}: ${text}`);
      }

      const rawText = await res.text();
      let data;
      try {
        data = JSON.parse(rawText);
      } catch (e) {
        data = rawText;
      }

      // Try multiple possible shapes in response
      let questions = [];
      if (Array.isArray(data?.questions)) questions = data.questions;
      else if (Array.isArray(data)) questions = data;
      else if (Array.isArray(data?.results)) questions = data.results;
      else if (Array.isArray(data?.data)) questions = data.data;
      else if (typeof data === "string" && data.trim().length > 0) {
        // Parse plain-text MCQ response into structured objects
        const normalize = (s) => this.normalizeMathText(s);

        const parsePlain = (text) => {
          const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
          const out = [];
          let cur = null;
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const qMatch = line.match(/^Q\s*\d+\.?\s*(.*)/i);
            // accept formats: A.  A)  A -  A:  A  (with space)
            const optMatch = line.match(/^([A-D])(?:[\.\)\-:\s]+)(.+)$/i);
            // Accept "Correct Answer: C", "Correct Answer - C", "Answer: Option C", or text answers
            const correctLetterMatch = line.match(/^(?:Correct\s*Answer|Answer)[:\-\s]*([A-D])\b/i);
            const correctTextMatch = line.match(/^(?:Correct\s*Answer|Answer)[:\-\s]*(.+)$/i);

            if (qMatch) {
              if (cur) out.push(cur);
              cur = { question: normalize(qMatch[1].trim()), options: {}, correct_answer: null, explanation: "" };
              continue;
            }

            if (optMatch && cur) {
              const key = optMatch[1].toUpperCase();
              cur.options[key] = normalize(optMatch[2].trim());
              // If we previously had a correct letter, ensure it's applied
              if (cur._pendingCorrectLetter) {
                cur.correct_answer = cur._pendingCorrectLetter;
                delete cur._pendingCorrectLetter;
              }
              continue;
            }

            if (correctLetterMatch && cur) {
              // If options already parsed, set directly; otherwise remember pending
              const letter = correctLetterMatch[1].toUpperCase();
              if (Object.keys(cur.options).length) cur.correct_answer = letter;
              else cur._pendingCorrectLetter = letter;
              continue;
            }

            if (correctTextMatch && cur) {
              const ansText = correctTextMatch[1].trim();
              const normAns = normalize(ansText).toLowerCase();
              // Try to match the answer text to one of the collected options
              let matched = false;
              for (const k of ["A", "B", "C", "D"]) {
                if (cur.options[k] && cur.options[k].toLowerCase().includes(normAns)) {
                  cur.correct_answer = k;
                  matched = true;
                  break;
                }
              }
              // If it looks like a single letter there, handle it
              const singleLetter = ansText.match(/^([A-D])$/i);
              if (!matched && singleLetter) {
                const letter = singleLetter[1].toUpperCase();
                if (Object.keys(cur.options).length) cur.correct_answer = letter;
                else cur._pendingCorrectLetter = letter;
                matched = true;
              }
              if (matched) continue;
            }

            // fallback: append to last question text if it looks like continuation
            if (cur && cur.question && !Object.keys(cur.options).length) {
              cur.question = normalize(cur.question + ' ' + line);
            } else if (cur && Object.keys(cur.options).length && !cur.correct_answer) {
              // treat as explanation lines
              cur.explanation = normalize((cur.explanation + ' ' + line).trim());
            }
          }
          if (cur) out.push(cur);
          return out;
        };

        questions = parsePlain(data);
      }

      // save cache when question_id present
      if (this.props.question_id) {
        this.saveToCache({
          storedAt: Date.now(),
          question_id: this.props.question_id,
          questions,
        });
      }

      this.setState({ similar: questions, loading: false, selectedOption: null, answered: false });
    } catch (err) {
      this.setState({
        error: err.message || String(err),
        loading: false,
      });
    }
  };

  openSimilarQuestions = () => {
    if (!this.props.question_id) {
      this.fetchSimilarFromApi();
      return;
    }

    const cached = this.loadFromCache();

    if (cached?.questions && Array.isArray(cached.questions) && cached.questions.length > 0) {
      this.setState({
        modalShow: true,
        similar: cached.questions,
        loading: false,
        error: null,
        currentIndex: 0,
        showExplanation: false,
        selectedOption: null,
        answered: false,
      });
    } else {
      this.fetchSimilarFromApi();
    }
  };

  componentDidUpdate(prevProps) {
    if (this.props.triggerQuery !== prevProps.triggerQuery) {
      this.openSimilarQuestions();
    }
  }

  // ✅ UI display using MathJax (render question/explanation nicely)
  renderMathContent = (text, variant = "normal") => {
    let md = text || "";

    // For explanation variant, ensure a leading blank line when the content
    // starts with a markdown heading or list so it doesn't stick to the box edge.
    if (variant === "small") {
      // If content starts with a heading, list, or numbered list, add a leading newline
      const startsWithBlock = /^\s*(#{1,6}\s|[-*+]\s|\d+\.\s)/.test(md);
      // Also handle cases where the AI returns a horizontal rule immediately followed
      // by a heading without a newline, e.g. "--- ####". Detect HR followed by optional
      // whitespace and a heading/list marker and treat as block-start.
      const hrFollowedByBlock = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*(?:#{1,6}\s|[-*+]\s|\d+\.\s)/.test(md);
      if (startsWithBlock || hrFollowedByBlock) md = "\n" + md;
    }

    // Let marked produce semantic HTML (keep block structure); don't strip newlines.
    const html = marked.parse(md || "");
    const style = variant === "small" ? styles.explanationText : styles.answerContent;
    return (
      <MathJax dynamic>
        <div
          style={style}
          dangerouslySetInnerHTML={{
            __html: html || "<p style='color:#777'>No content available.</p>",
          }}
        />
      </MathJax>
    );
  };

  nextQuestion = () => {
    this.setState((prev) => ({
      currentIndex: Math.min(prev.currentIndex + 1, prev.similar.length - 1),
      showExplanation: false,
      showDetailedExplanation: false,
      selectedOption: null,
      answered: false,
    }));
  };

  prevQuestion = () => {
    this.setState((prev) => ({
      currentIndex: Math.max(prev.currentIndex - 1, 0),
      showExplanation: false,
      showDetailedExplanation: false,
      selectedOption: null,
      answered: false,
    }));
  };

  // Build an MCQ text block from a question object for the explain API
  buildMcqText = (qObj) => {
    if (!qObj) return "";
    const parts = [];
    parts.push(`Q. ${qObj.question}`);
    const opts = qObj.options || {};
    for (const k of ["A", "B", "C", "D"]) {
      if (opts[k]) parts.push(`${k}. ${opts[k]}`);
    }
    if (qObj.correct_answer) parts.push(`Correct Answer: ${qObj.correct_answer}`);
    return parts.join(" \n");
  };

  fetchExplanationForCurrent = async () => {
    const { similar, currentIndex } = this.state;
    const current = similar[currentIndex];
    if (!current) return;

    // If we already have an explanation, just toggle show
    if (current.explanation && current.explanation.trim() !== "") {
      this.setState({ showExplanation: true });
      return;
    }

    const mcqText = this.buildMcqText(current);
    const url = `https://ai.rizee.in/explain-mcq-3?mcq=${this.encodeForm(mcqText)}&student_class=&subject=`;

    // open explanation area immediately and show its loading spinner
    // Keep detailed explanation hidden until the user requests it via button.
    this.setState({ explanationLoading: true, error: null, showExplanation: true, showDetailedExplanation: false });
    try {
      const res = await fetch(url, { method: "GET" });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Explain API error ${res.status}: ${t}`);
      }

      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch (e) {
        data = raw;
      }

      // accept keys 'exaplation' (typo) or 'explanation'
      let explanation = null;
      if (data && typeof data === "object") {
        explanation = data.exaplation || data.explanation || data.explanations || null;
        if (Array.isArray(explanation)) explanation = explanation.join("\n\n");
      } else if (typeof data === "string") {
        explanation = data;
      }

      explanation = this.normalizeMathText(explanation || "No explanation provided.");

      // update similar[currentIndex].explanation immutably and persist to cache
      this.setState((prev) => {
        const newSimilar = prev.similar.map((s, idx) => {
          if (idx !== prev.currentIndex) return s;
          return { ...s, explanation };
        });
        // Persist updated explanations to cache when a question_id is present
        if (this.props.question_id) {
          try {
            this.saveToCache({ storedAt: Date.now(), question_id: this.props.question_id, questions: newSimilar });
          } catch (e) {
            // ignore cache failures
          }
        }

        // after short explanation is received, keep detailed hidden until requested
        return { similar: newSimilar, explanationLoading: false, showExplanation: true, showDetailedExplanation: false };
      });
    } catch (err) {
      this.setState({ error: err.message || String(err), explanationLoading: false });
    }
  };

  // Fetch detailed explanation from a separate API (configurable via prop)
  fetchDetailedExplanationForCurrent = async () => {
    const { similar, currentIndex } = this.state;
    const current = similar[currentIndex];
    if (!current) return;

    // If we already have a detailed explanation, just show it and hide short explanation
    if (current.detailed_explanation && current.detailed_explanation.trim() !== "") {
      this.setState({ showDetailedExplanation: true, showExplanation: false });
      return;
    }

    const mcqText = this.buildMcqText(current);
    // endpoint can be passed as prop `detailedExplainUrl`, otherwise default
    const endpoint = this.props.detailedExplainUrl || "https://ai.rizee.in/explain-mcq-detailed-3";
    const url = `${endpoint}?mcq=${this.encodeForm(mcqText)}&student_class=&subject=`;

    // open detailed area immediately and show its loading spinner
    // Hide the short explanation when displaying detailed content (mutually exclusive)
    this.setState({ detailedLoading: true, error: null, showDetailedExplanation: true, showExplanation: false });
    try {
      const res = await fetch(url, { method: "GET" });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Detailed Explain API error ${res.status}: ${t}`);
      }

      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch (e) {
        data = raw;
      }

      let detailed = null;
      if (data && typeof data === "object") {
        detailed = data.exaplation || data.explanation || data.detailed_explanation || data.explanations || null;
        if (Array.isArray(detailed)) detailed = detailed.join("\n\n");
      } else if (typeof data === "string") {
        detailed = data;
      }

      detailed = this.normalizeMathText(detailed || "No detailed explanation provided.");

      // update similar[currentIndex].detailed_explanation immutably and persist to cache
      this.setState((prev) => {
        const newSimilar = prev.similar.map((s, idx) => {
          if (idx !== prev.currentIndex) return s;
          return { ...s, detailed_explanation: detailed };
        });

        if (this.props.question_id) {
          try {
            this.saveToCache({ storedAt: Date.now(), question_id: this.props.question_id, questions: newSimilar });
          } catch (e) {
            // ignore
          }
        }

        return { similar: newSimilar, detailedLoading: false, showDetailedExplanation: true, showExplanation: false };
      });
    } catch (err) {
      this.setState({ error: err.message || String(err), detailedLoading: false });
    }
  };

  render() {

    
    const { modalShow, similar, loading, error, currentIndex, showExplanation, showDetailedExplanation, answered } = this.state;
    const current = similar[currentIndex] || null;

    const config = {
      loader: { load: ["[tex]/html"] },
      tex: {
        inlineMath: [["$", "$"], ["\\(", "\\)"]],
        displayMath: [["$$", "$$"], ["\\[", "\\]"]],
      },
      svg: { fontCache: "global" },
    };

    return (
      <MathJaxContext config={config}>
        <style>
          {`
            @media (max-width: 576px) {
              .ai-options-grid {
                grid-template-columns: 1fr !important;
              }
            }

            /* ✅ Compact header */
            .ai-modal-header-compact {
              padding: 10px 14px !important;
              min-height: 44px !important;
            }

            .ai-modal-title-compact {
              font-size: 15px !important;
              font-weight: 900 !important;
              margin: 0 !important;
              line-height: 1.2 !important;
            }

            /* Explanation area: force smaller font and scale MathJax output
               We apply multiple fallbacks (inherit, direct selectors and transform)
               to override MathJax's internal sizing and inline styles. */
            .ai-explanation { font-size: 12px !important; line-height: 1.35rem !important; }
            .ai-explanation, .ai-explanation * { font-size: 12px !important; line-height: 1.35rem !important; }

            /* Reduce sizes for markdown elements coming from the AI response */
            .ai-explanation h1, .ai-explanation h2, .ai-explanation h3, .ai-explanation h4, .ai-explanation h5, .ai-explanation h6 {
              font-size: 14px !important;
              margin-top: 8px !important;
              margin-bottom: 6px !important;
              line-height: 1.25 !important;
              font-weight: 800 !important;
            }

            .ai-explanation p, .ai-explanation li {
              font-size: 12px !important;
              margin: 4px 0 !important;
              color: #92400e !important;
            }

            .ai-explanation ul, .ai-explanation ol { padding-left: 18px !important; margin: 6px 0 !important; }
            .ai-explanation hr { border: 0 !important; border-top: 1px solid rgba(0,0,0,0.06) !important; margin: 12px 0 !important; }

            /* Target MathJax renderers and force inheritence/scale */
            .ai-explanation .mjx-chtml, .ai-explanation .mjx-svg, .ai-explanation .MathJax { font-size: 12px !important; }
            .ai-explanation .mjx-chtml, .ai-explanation .mjx-svg { transform: scale(0.82); transform-origin: 0 0; }
            .ai-explanation .mjx-chtml * { font-size: inherit !important; }
            .ai-explanation .mjx-svg svg { width: auto !important; height: auto !important; }
            .ai-explanation svg { max-width: 100% !important; height: auto !important; }
          `}
        </style>

        <Modal show={modalShow} onHide={this.close} size="lg" centered>
          <Modal.Header closeButton className="ai-modal-header-compact">
            <Modal.Title className="ai-modal-title-compact">
              📝 Similar Questions (MCQs):
            </Modal.Title>
          </Modal.Header>

          <Modal.Body style={styles.modalBody}>
            {loading && (!similar || similar.length === 0) && (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <div className="mt-2">Loading similar questions...</div>
              </div>
            )}

            {!loading && error && <Alert variant="danger">{error}</Alert>}

            {!loading && !error && similar.length === 0 && (
              <Alert variant="warning">No similar questions found.</Alert>
            )}

            {!loading && !error && current && (
              <>
                {/* ✅ Top Question (MathJax display) */}
                <div style={styles.topBar}>
                  <div style={styles.counterChip}>
                    {currentIndex + 1} / {similar.length}
                  </div>

                  <div style={styles.topQuestionBox}>
                    {/* <div style={{ fontWeight: 900, marginBottom: "6px" }}>Q:</div> */}
                    <div style={{ fontWeight: 600 }}>{this.renderMathContent(current.question)}</div>
                  </div>
                </div>

                            <div style={styles.outputCard}>
                              {/* ✅ Options */}
                              <div style={styles.answerBox}>
                                <div style={styles.label}>✅ Choose the correct option</div>

                                <div className="ai-options-grid" style={styles.optionsGrid}>
                                  {Object.entries(current.options || {}).map(([key, value]) => {
                                    const { selectedOption, answered } = this.state;
                                    const correctKey = current.correct_answer || null;
                                    const isCorrect = correctKey && key === correctKey;
                                    const isSelected = selectedOption === key;

                                    // Compute dynamic styles
                                    let cardStyle = { ...styles.optionCard };
                                    if (answered) {
                                      if (isCorrect) cardStyle = { ...cardStyle, ...styles.correctOptionCard };
                                      else if (isSelected && !isCorrect) cardStyle = { ...cardStyle, ...styles.wrongOptionCard };
                                      else cardStyle = { ...cardStyle };
                                    }

                                    return (
                                      <div
                                        key={key}
                                        style={{ cursor: answered ? "default" : "pointer", ...cardStyle }}
                                        onClick={() => this.handleSelectOption(key)}
                                      >
                                        <div style={styles.optionInner}>
                                          <b style={styles.optionKey}>{key}.</b>
                                          <div style={styles.optionValue}>{this.renderMathContent(value)}</div>
                                        </div>
                                        {answered && isCorrect && <span style={styles.tick}>✔</span>}
                                        {answered && isSelected && !isCorrect && <span style={styles.cross}>✖</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              
                  <div style={styles.actionsRow}>
                    {this.state.answered ? (
                      <>
                        <button
                          style={showExplanation ? styles.dangerButton : styles.smallButton}
                          onClick={() => {
                            if (showExplanation) {
                              this.setState({ showExplanation: false });
                            } else {
                              // hide detailed immediately, then fetch short explanation
                              this.setState({ showDetailedExplanation: false }, () => this.fetchExplanationForCurrent());
                            }
                          }}
                        >
                          {showExplanation ? (
                            <>
                              <span style={{ marginRight: 8 }}>🧠</span>Hide Explanation
                            </>
                          ) : (
                            <>
                              <span style={{ marginRight: 8 }}>🧠</span>Explain
                            </>
                          )}
                        </button>

                        {/* Detailed Explain button - visible once user answered (always shown alongside Explain) */}
                        <button
                          style={{ ...styles.smallButton, marginLeft: 8, background: "#059669", border: "1px solid #059669" }}
                          onClick={() => {
                            if (this.state.showDetailedExplanation) {
                              this.setState({ showDetailedExplanation: false });
                            } else {
                              // hide short explanation immediately, then fetch detailed
                              this.setState({ showExplanation: false }, () => this.fetchDetailedExplanationForCurrent());
                            }
                          }}
                        >
                          {this.state.showDetailedExplanation ? (
                            <>
                              <span style={{ marginRight: 8 }}>📘</span>Hide Detailed
                            </>
                          ) : (
                            <>
                              <span style={{ marginRight: 8 }}>📘</span>Detailed Explain
                            </>
                          )}
                        </button>
                      </>
                    ) : null}
                  </div>

                  {/* ✅ Explanation */}
                  {showExplanation && (
                    <div style={styles.explanationBox} className="ai-explanation">
                      <div style={styles.explanationHeading}>📌 Explanation</div>
                      <div style={styles.explanationText}>
                        {this.state.explanationLoading ? (
                          <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>
                        ) : (
                          this.renderMathContent(current.explanation, 'small')
                        )}
                      </div>
                    </div>
                  )}

                  {/* Detailed Explanation */}
                  {this.state.showDetailedExplanation && (
                    <div style={{ ...styles.explanationBox, marginTop: 10 }} className="ai-explanation">
                      <div style={styles.explanationHeading}>🔎 Detailed Explanation</div>
                      <div style={styles.explanationText}>
                        {this.state.detailedLoading ? (
                          <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>
                        ) : (
                          this.renderMathContent((current && current.detailed_explanation) || "", 'small')
                        )}
                      </div>
                    </div>
                  )}

                  {/* ✅ Navigation */}
                  <div style={styles.navRow}>
                    <button
                      style={currentIndex === 0 ? styles.disabledNavBtn : styles.navBtn}
                      onClick={this.prevQuestion}
                      disabled={currentIndex === 0}
                    >
                      ⬅ Prev
                    </button>

                    <button
                      style={currentIndex === similar.length - 1 ? styles.disabledNavBtn : styles.navBtn}
                      onClick={this.nextQuestion}
                      disabled={currentIndex === similar.length - 1}
                    >
                      Next ➡
                    </button>
                  </div>
                </div>
              </>
            )}
          </Modal.Body>
        </Modal>
      </MathJaxContext>
    );
  }
}

/* ✅ FINAL UI STYLES */
const styles = {
  modalBody: {
    background: "linear-gradient(180deg,#f8fafc,#eef2ff)",
    padding: "18px",
  },

  topBar: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginBottom: "12px",
  },

  counterChip: {
    alignSelf: "flex-start",
    background: "rgba(79,70,229,0.14)",
    border: "1px solid rgba(79,70,229,0.25)",
    color: "#312e81",
    padding: "6px 12px",
    borderRadius: "999px",
    fontWeight: 900,
    fontSize: "12px",
  },

  topQuestionBox: {
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(99,102,241,0.18)",
    padding: "10px 12px",
    borderRadius: "12px",
    fontWeight: 800,
    color: "#0f172a",
    fontSize: "14px",
    lineHeight: "1.4rem",
  },

  outputCard: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(15,23,42,0.12)",
    padding: "18px",
    border: "1px solid rgba(99,102,241,0.18)",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },

  answerBox: {
    background: "#fff",
    padding: "12px",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
  },

  label: {
    fontWeight: 900,
    fontSize: "13px",
    marginBottom: "8px",
    color: "#475569",
  },

  optionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "8px",
  },

  optionCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    padding: "8px 10px",
    borderRadius: "10px",
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
    fontSize: "14px",
    lineHeight: "1.2rem",
  },

  correctOptionCard: {
    background: "rgba(16,185,129,0.12)",
    border: "1px solid rgba(16,185,129,0.40)",
    color: "#065f46",
    fontWeight: 800,
  },

  optionInner: {
    display: "flex",
    gap: "8px",
    flex: 1,
  },

  optionKey: {
    minWidth: "22px",
    fontWeight: 900,
  },

  optionValue: {
    flex: 1,
    fontWeight: 600,
  },

  tick: {
    fontWeight: 900,
  },

  actionsRow: {
    display: "flex",
    justifyContent: "flex-end",
  },

  smallButton: {
    padding: "8px 14px",
    borderRadius: "999px",
    border: "1px solid #6366f1",
    background: "#6366f1",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },

  dangerButton: {
    padding: "8px 14px",
    borderRadius: "999px",
    border: "1px solid #ef4444",
    background: "#ef4444",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },

  wrongOptionCard: {
    background: "rgba(239,68,68,0.06)",
    border: "1px solid rgba(239,68,68,0.22)",
    color: "#7f1d1d",
    fontWeight: 800,
  },

  cross: {
    fontWeight: 900,
    color: "#7f1d1d",
  },

  explanationBox: {
    background: "#fef3c7",
    border: "1px solid #fcd34d",
    padding: "14px",
    borderRadius: "14px",
  },

  explanationHeading: {
    fontWeight: 900,
    fontSize: "12px",
    marginBottom: "8px",
    color: "#92400e",
  },

  explanationText: {
    color: "#92400e",
    fontSize: "12px",
    lineHeight: "1.35rem",
  },

  navRow: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "6px",
    gap: "10px",
  },

  navBtn: {
    padding: "10px 14px",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontWeight: 900,
    cursor: "pointer",
    minWidth: "120px",
  },

  disabledNavBtn: {
    padding: "10px 14px",
    borderRadius: "12px",
    background: "#e5e7eb",
    fontWeight: 900,
    opacity: 0.6,
    cursor: "not-allowed",
    minWidth: "120px",
  },

  answerContent: {
    lineHeight: "1.7rem",
    fontSize: "14px",
  },
};

export default AiSimilarQuestionModels2;
